/**
 * 消息通知全局状态管理
 *
 * 负责管理：
 * - 全局未读数量
 * - 菜单微标更新
 * - 跨组件状态同步
 */

import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import { notificationApi } from '#/api/modules';

export const useNotificationStore = defineStore('notification', () => {
  // 状态
  const unreadCount = ref(0);
  const loading = ref(false);
  const lastUpdateTime = ref<Date | null>(null);

  // 计算属性
  const hasUnread = computed(() => unreadCount.value > 0);
  const badgeText = computed(() => {
    if (unreadCount.value === 0) return '';
    if (unreadCount.value > 99) return '99+';
    return unreadCount.value.toString();
  });

  // 获取未读数量
  async function fetchUnreadCount() {
    try {
      loading.value = true;
      const response = await notificationApi.getUnreadCount();
      unreadCount.value = response.count;
      lastUpdateTime.value = new Date();
    } catch (error) {
      console.error('获取未读数量失败:', error);
    } finally {
      loading.value = false;
    }
  }

  // 减少未读数量（标记已读后调用）
  function decreaseUnreadCount(count: number = 1) {
    unreadCount.value = Math.max(0, unreadCount.value - count);
  }

  // 增加未读数量（收到新消息后调用）
  function increaseUnreadCount(count: number = 1) {
    unreadCount.value += count;
  }

  // 重置未读数量（标记全部已读后调用）
  function resetUnreadCount() {
    unreadCount.value = 0;
  }

  // 设置未读数量（直接设置）
  function setUnreadCount(count: number) {
    unreadCount.value = Math.max(0, count);
  }

  // 重置所有状态（用于用户登出）
  function resetAll() {
    unreadCount.value = 0;
    loading.value = false;
    lastUpdateTime.value = null;
  }

  return {
    // 状态
    unreadCount,
    loading,
    lastUpdateTime,

    // 计算属性
    hasUnread,
    badgeText,

    // 方法
    fetchUnreadCount,
    decreaseUnreadCount,
    increaseUnreadCount,
    resetUnreadCount,
    setUnreadCount,
    resetAll,
  };
});
